import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { parseAsInteger, useQueryState } from 'nuqs';
import { ArrowUpRight, MapPin, User } from 'lucide-react';
import { SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { orderService } from '@/services/orderService';
import type { Order } from '@/types/domain';
import { OrderJourney } from './OrderJourney';
import { OrderNotesPanel } from './OrderNotesPanel';
import { OrderItemsCompact } from './OrderItemsCompact';
import { getOrderStatusClasses } from './order-journey';

/**
 * Order sheet — a peek at an order from any surface without losing your place.
 *
 * Opening an order currently means leaving a paginated, filtered, URL-stateful
 * table and then rebuilding that state on the way back. On a 366-row queue that
 * cost is high enough that people skip the check entirely — which is how
 * delivery constraints in the order notes end up never reaching the packer.
 *
 * State lives in the `?order=` query param so the sheet is linkable, survives a
 * refresh, and closes on browser-back.
 */

/** The row data a caller already has — renders instantly while the rest loads. */
export interface OrderSeed {
  salesorder_number?: string | null;
  customer_name?: string | null;
  total?: number | null;
  status?: string | null;
}

interface OrderSheetContextValue {
  orderId: number | null;
  openOrder: (id: number, seed?: OrderSeed) => void;
  closeOrder: () => void;
}

const OrderSheetContext = createContext<OrderSheetContextValue | null>(null);

export function useOrderSheet(): OrderSheetContextValue {
  const ctx = useContext(OrderSheetContext);
  if (!ctx) {
    throw new Error('useOrderSheet must be used within an <OrderSheetProvider>');
  }
  return ctx;
}

export function OrderSheetProvider({ children }: { children: ReactNode }) {
  const [orderId, setOrderId] = useQueryState(
    'order',
    parseAsInteger.withOptions({ history: 'push', clearOnDefault: true }),
  );
  const [seed, setSeed] = useState<OrderSeed | null>(null);

  const openOrder = useCallback(
    (id: number, nextSeed?: OrderSeed) => {
      setSeed(nextSeed ?? null);
      void setOrderId(id);
    },
    [setOrderId],
  );

  const closeOrder = useCallback(() => {
    void setOrderId(null);
  }, [setOrderId]);

  const value = useMemo(
    () => ({ orderId, openOrder, closeOrder }),
    [orderId, openOrder, closeOrder],
  );

  return (
    <OrderSheetContext.Provider value={value}>
      {children}
      <OrderSheet orderId={orderId} seed={seed} onClose={closeOrder} />
    </OrderSheetContext.Provider>
  );
}

function formatCurrency(amount: number, currency = 'GBP'): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount);
}

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function BodySkeleton() {
  return (
    <div className="space-y-4" aria-hidden>
      <div className="h-16 animate-pulse rounded-md bg-muted" />
      <div className="h-12 animate-pulse rounded-md bg-muted" />
      <div className="space-y-2">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    </div>
  );
}

function OrderSheet({
  orderId,
  seed,
  onClose,
}: {
  orderId: number | null;
  seed: OrderSeed | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (orderId === null) return;

    let cancelled = false;
    setOrder(null);
    setError(null);
    setLoading(true);

    orderService
      .getById(orderId)
      .then((data) => {
        if (!cancelled) setOrder(data);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load order for sheet:', err);
          setError('Could not load this order.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [orderId]);

  // Prefer live data, fall back to the seed so the header never renders empty.
  const number = order?.salesorder_number ?? seed?.salesorder_number ?? '';
  const customer = order?.customer_name ?? seed?.customer_name ?? '';
  const status = order?.status ?? seed?.status ?? null;
  const total = order?.total ?? seed?.total ?? null;
  const currency = order?.currency_code ?? 'GBP';
  const shipTo = order?.shipping_address_json ?? null;

  const goToFullOrder = useCallback(() => {
    if (orderId === null) return;
    onClose();
    navigate(`/order/${orderId}`);
  }, [navigate, onClose, orderId]);

  const goToCustomer = useCallback(() => {
    if (!order?.zoho_customer_id) return;
    onClose();
    // customerService.getById resolves zoho ids as well as numeric ids,
    // which is how ViewOrder looks the customer up.
    navigate(`/customers/${order.zoho_customer_id}`);
  }, [navigate, onClose, order?.zoho_customer_id]);

  return (
    <SheetContent
      isOpen={orderId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      side="right"
      isFloat={false}
      aria-label={number ? `Order ${number}` : 'Order details'}
      className={cn(
        'flex flex-col gap-0 sm:max-w-xl',
        'entering:duration-300 exiting:duration-200',
      )}
    >
      {/* Header — renders from seed data, so the sheet never opens blank */}
      <header className="shrink-0 border-b border-border px-5 py-4 pr-12">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-foreground">
                {number || 'Order'}
              </h2>
              {status && (
                <span
                  className={cn(
                    'rounded-md border px-1.5 py-0.5 text-xs font-medium capitalize',
                    getOrderStatusClasses(status),
                  )}
                >
                  {status.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            {customer && (
              <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-muted-foreground">
                <User size={12} className="shrink-0" />
                {customer}
              </p>
            )}
          </div>
          {total !== null && (
            <div className="shrink-0 text-right">
              <p className="text-lg font-semibold tabular-nums text-foreground">
                {formatCurrency(total, currency)}
              </p>
              {order?.date && (
                <p className="text-xs tabular-nums text-muted-foreground">
                  {formatDate(order.date)}
                </p>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
        {error ? (
          <div className="rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : loading && !order ? (
          <BodySkeleton />
        ) : order ? (
          <>
            {/* Constraints first — this is what the packer needs before packing */}
            <OrderNotesPanel notes={order.notes} deliveryDate={order.delivery_date} />

            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Progress
              </h3>
              <OrderJourney order={order} orientation="horizontal" />
            </section>

            {shipTo && (shipTo.address || shipTo.city || shipTo.zip) && (
              <section>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Ship to
                </h3>
                <p className="flex items-start gap-1.5 text-sm leading-relaxed text-foreground">
                  <MapPin size={13} className="mt-0.5 shrink-0 text-muted-foreground" />
                  <span>
                    {[shipTo.address, shipTo.street2, shipTo.city, shipTo.zip, shipTo.country]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </p>
              </section>
            )}

            <section>
              <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Items{' '}
                <span className="tabular-nums font-normal">
                  ({order.line_items?.length ?? 0})
                </span>
              </h3>
              <OrderItemsCompact items={order.line_items} currency={currency} />
            </section>
          </>
        ) : null}
      </div>

      {/* Footer */}
      <footer className="flex shrink-0 items-center gap-2 border-t border-border px-5 py-3">
        <Button intent="outline" size="sm" onPress={goToFullOrder} isDisabled={orderId === null}>
          Open full order
          <ArrowUpRight size={13} />
        </Button>
        {order?.zoho_customer_id && (
          <Button intent="plain" size="sm" onPress={goToCustomer}>
            View customer
          </Button>
        )}
      </footer>
    </SheetContent>
  );
}

export default OrderSheetProvider;
